import React, { useMemo, useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { getDeviceTypeColor, getDeviceTypeName } from '@/utils/deviceColorUtils';

interface Schedule {
  ID: number;
  DeviceID: string;
  Index: number;
  Name: string;
  PumpSpeed: number;
  AuxType: number;
  AuxSlot: number;
  IsScheduleOn: boolean;
  StartTime: number; // Minutes from midnight
  EndTime: number; // Minutes from midnight
  Weekdays?: number;
  WeekdaysArray?: boolean[];
}

interface DeviceScheduleTimelineProps {
  schedules: Schedule[][];  // Modified to 2D array, each element is the schedule array for the corresponding week
  deviceName: string;
}

// Days of the week
const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

// Define ScheduleInfo interface
interface ScheduleInfo {
  name: string;
  startTime: string;
  endTime: string;
  start: number;
  end: number;
}

const DeviceScheduleTimeline: React.FC<DeviceScheduleTimelineProps> = ({ schedules, deviceName }) => {
  const theme = useTheme();
  const [currentTimeLine, setCurrentTimeLine] = useState<number>(0);
  
  // 更新当前时间线
  useEffect(() => {
    // 计算当前时间（以分钟为单位）
    const updateCurrentTime = () => {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      setCurrentTimeLine(minutes);
    };
    
    // 立即更新一次
    updateCurrentTime();
    
    // 每分钟更新一次
    const intervalId = setInterval(updateCurrentTime, 60000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Convert minutes to formatted time (HH:MM)
  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Process data for the chart
  const chartData = useMemo(() => {
    // 为每个设备创建独立的数据系列
    const seriesMap: Record<string, any> = {};
    
    // 处理每一天的日程
    schedules.forEach((daySchedules, dayIndex) => {
      daySchedules.forEach(schedule => {
        // 使用工具函数获取设备颜色
        const color = getDeviceTypeColor(schedule.AuxType);
        
        // 创建唯一设备ID - 基于设备类型而不是名称
        // 这样相同类型的设备会合并到同一个系列，使图例和实际颜色对应
        const deviceType = schedule.Name;
        const deviceId = `${deviceType}-${schedule.AuxType}`;
        
        // 如果数据系列不存在，则创建新系列
        if (!seriesMap[deviceId]) {
          seriesMap[deviceId] = {
            name: deviceType,
            color: color, // 为整个系列设置固定颜色
            data: []
          };
        }
        
        // 简化处理，不考虑跨午夜情况
        seriesMap[deviceId].data.push({
          x: DAYS_OF_WEEK[dayIndex],
          y: [schedule.StartTime, schedule.EndTime],
          deviceInfo: {
            name: schedule.Name,
            slot: schedule.AuxSlot,
            type: schedule.AuxType,
            typeName: deviceType,
            startTime: formatTime(schedule.StartTime),
            endTime: formatTime(schedule.EndTime),
            isOn: schedule.IsScheduleOn
          },
          fillColor: color
        });
      });
    });
    
    // 为每个星期几添加一个隐形点，但不会显示在图例中
    // 记录已经有数据的星期几
    const daysWithData = new Set();
    Object.values(seriesMap).forEach(series => {
      series.data.forEach((item: any) => {
        daysWithData.add(item.x);
      });
    });
    
    // 为没有数据的星期几添加透明数据点到第一个系列
    const result = Object.values(seriesMap);
    if (result.length > 0) {
      const firstSeries = result[0];
      DAYS_OF_WEEK.forEach(day => {
        if (!daysWithData.has(day)) {
          firstSeries.data.push({
            x: day,
            y: [0, 0],
            fillColor: 'transparent',
            opacity: 0,
            strokeWidth: 0,
            deviceInfo: {
              name: 'Empty',
              slot: 0,
              type: 0,
              typeName: '',
              startTime: '',
              endTime: '',
              isOn: false
            },
            showInLegend: false
          });
        }
      });
    } else {
      // 如果没有任何系列，创建一个全透明的系列来显示所有星期几
      result.push({
        name: '',
        showInLegend: false,
        data: DAYS_OF_WEEK.map(day => ({
          x: day,
          y: [0, 1],
          fillColor: 'transparent',
          opacity: 0,
          strokeWidth: 0,
          deviceInfo: {
            name: '',
            slot: 0,
            type: 0,
            typeName: '',
            startTime: '',
            endTime: '',
            isOn: false
          }
        }))
      });
    }
    
    return result;
  }, [schedules]);

  // Chart options
  const chartOptions: ApexOptions = {
    chart: {
      type: 'rangeBar',
      height: 550,
      toolbar: {
        show: false
      },
      animations: {
        enabled: false
      },
      fontFamily: theme.typography.fontFamily,
      zoom: {
        enabled: false
      },
      selection: {
        enabled: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '85%',
        rangeBarGroupRows: false,
        rangeBarOverlap: false,
        distributed: false,
        borderRadius: 0,
        columnWidth: '100%'
      }
    },
    xaxis: {
      type: 'numeric',
      min: 0,
      max: 1440, // 24 hours in minutes
      position: 'top',
      tickAmount: 24,
      labels: {
        formatter: function(val: string, timestamp?: number) {
          return `${Math.floor(parseInt(val) / 60)}:00`;
        },
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px'
        }
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      custom: function({seriesIndex, dataPointIndex, w}) {
        const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        const deviceInfo = data.deviceInfo;
        
        return `
          <div class="apexcharts-tooltip-title" style="font-family: ${theme.typography.fontFamily}; padding: 8px; background: ${theme.palette.background.paper}; border-bottom: 1px solid ${theme.palette.divider};">
            <b>${deviceInfo.name}</b> <span style="font-size: 9px; opacity: 0.8;"></span>
          </div>
          <div style="padding: 8px; font-family: ${theme.typography.fontFamily};">
            <div><span style="font-weight: bold;">Time:</span> ${deviceInfo.startTime} - ${deviceInfo.endTime}</div>
            <div><span style="font-weight: bold;">Status:</span> <span style="color: ${deviceInfo.isOn ? '#22c55e' : '#ef4444'}">${deviceInfo.isOn ? 'Enabled' : 'Disabled'}</span></div>
          </div>
        `;
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '10px',
      fontFamily: theme.typography.fontFamily,
      labels: {
        colors: theme.palette.text.primary
      },
      itemMargin: {
        horizontal: 8
      },
      markers: {
        size: 8,
        strokeWidth: 0,
        shape: 'circle'
      },
      height: 30,
      onItemClick: {
        toggleDataSeries: false
      },
      onItemHover: {
        highlightDataSeries: true
      }
    },
    grid: {
      borderColor: theme.palette.divider,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      row: {
        colors: [theme.palette.background.paper, theme.palette.action.selected],
        opacity: 0.8
      }
    },
    stroke: {
      width: 1
    },
    fill: {
      type: 'solid',
      opacity: 0.8
    },
    dataLabels: {
      enabled: false
    },
    theme: {
      mode: theme.palette.mode
    },
    annotations: {
      xaxis: [
        {
          x: currentTimeLine,
          strokeDashArray: 5,
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
          label: {
            borderColor: theme.palette.primary.main,
            style: {
              color: '#fff',
              background: theme.palette.primary.main,
              fontSize: '10px',
              fontWeight: 'bold',
              fontFamily: theme.typography.fontFamily
            },
            orientation: 'horizontal',
            offsetY: 20,
            position: 'bottom',
            text: 'Now'
          }
        }
      ]
    }
  };
  
  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      minHeight: '50vh',
      display: 'flex',
      flexDirection: 'column',
      '& .apexcharts-tooltip': {
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`
      },
      '& .apexcharts-rangebar-area': {
        height: '30px !important'
      },
      '& .apexcharts-series': {
        margin: '5px 0'
      }
    }}>
      <ReactApexChart 
        options={chartOptions} 
        series={chartData} 
        type="rangeBar" 
        height="100%" 
        width="100%" 
      />
    </Box>
  );
};

export default DeviceScheduleTimeline; 